import React, { useState, useEffect } from 'react';
import { CalendarDays } from 'lucide-react';
import * as api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useEventsEnabled } from '../hooks/useEventsEnabled';
import { kindOf, formatPrice, formatEventDate, formatEventTime } from '../lib/eventsFormat';

const ymd = (d) => d.toLocaleDateString('en-CA');

/** Next few events, shown in the right sidebar under Dua of the Day. Hidden until events are switched on. */
const UpcomingEventsCard = ({ onNavigate }) => {
  const { profile } = useAuth();
  const enabled = useEventsEnabled();
  const visible = enabled === true || profile?.is_admin === true;
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    const start = new Date();
    const end = new Date(start.getTime() + 120 * 86400000);
    api.getEvents(ymd(start), ymd(end))
      .then((res) => {
        if (!active) return;
        const now = Date.now();
        setEvents((res.data || []).filter((e) => new Date(e.ends_at || e.starts_at).getTime() >= now).slice(0, 3));
      })
      .catch(() => { if (active) setEvents([]); });
    return () => { active = false; };
  }, [visible]);

  if (!visible || events === null) return null;

  return (
    <section className="glass-card rounded-arch-xl p-5" aria-labelledby="upcoming-events-title" data-testid="upcoming-events-card">
      <h3 id="upcoming-events-title" className="mb-1">
        <button
          onClick={() => onNavigate('calendar')}
          className="min-h-[44px] flex items-center gap-2 font-heading font-medium text-primary hover:opacity-80 transition-opacity"
          data-testid="upcoming-events-calendar-link"
        >
          <CalendarDays className="w-4 h-4" aria-hidden="true" />
          Upcoming Events
        </button>
      </h3>
      {events.length === 0 ? (
        <p className="text-sm text-text-muted pb-1">No events are scheduled yet. Check the calendar soon.</p>
      ) : (
        <ul className="space-y-1">
          {events.map((ev) => {
            const kind = kindOf(ev);
            return (
              <li key={ev.id}>
                <button
                  onClick={() => onNavigate(`event:${ev.id}`)}
                  className="w-full flex items-center gap-3 text-left p-2.5 rounded-arch hover:bg-teal/10 transition-colors"
                  data-testid={`upcoming-event-${ev.id}`}
                >
                  <span className={`w-[50px] h-[58px] shrink-0 rounded-t-full rounded-b-[14px] bg-gradient-to-b ${kind.date} text-white flex flex-col items-center justify-center leading-none`} aria-hidden="true">
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">{formatEventDate(ev, { month: 'short' })}</span>
                    <span className="font-heading text-xl font-semibold mt-0.5">{formatEventDate(ev, { day: 'numeric' })}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-heading font-semibold text-[14.5px] leading-snug text-text-main">{ev.title}</span>
                    <span className="block text-[12.5px] text-text-muted mt-0.5">
                      {ev.event_type === 'course_launch' ? 'Course launch' : formatEventTime(ev).split(' to ')[0]}
                      {ev.event_type === 'in_person' && ev.city ? ` · ${ev.city}` : ''}
                      {ev.event_type === 'online' ? ' · Online' : ''}
                    </span>
                  </span>
                  <span className="text-[12.5px] font-bold text-teal-dark dark:text-teal-light whitespace-nowrap">{ev.is_ticketed ? formatPrice(ev.your_price_cents) : 'Free'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default UpcomingEventsCard;
